import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from './customFetcher';
import { ITag, ITagInput } from '../Interfaces/ITag';

const TAGS_QUERY_KEY = ['tags'] as const;

export const useGetTags = () => {
    const { data: tags = [], isLoading: isLoadingTags } = useQuery<ITag[]>({
        queryKey: TAGS_QUERY_KEY,
        queryFn: () => customFetcher<ITag[]>({ endpoint: '/tags/' }),
        staleTime: 5 * 60 * 1000,
    });
    return { tags, isLoadingTags };
};

export const useCreateTag = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: createTag, isPending: isCreatingTag } = useMutation<ITag, Error, ITagInput>({
        mutationFn: (payload) => customFetcher<ITag>({ endpoint: '/tags/', method: 'POST', body: payload }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: TAGS_QUERY_KEY });
        },
    });
    return { createTag, isCreatingTag };
};

export const useUpdateTag = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateTag, isPending: isUpdatingTag } = useMutation<ITag, Error, ITag>({
        mutationFn: (tag) =>
            customFetcher<ITag>({
                endpoint: `/tags/${tag.id}/`,
                method: 'PUT',
                body: { name: tag.name, color: tag.color },
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: TAGS_QUERY_KEY });
        },
    });
    return { updateTag, isUpdatingTag };
};

export const useDeleteTag = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: deleteTag, isPending: isDeletingTag } = useMutation<void, Error, number>({
        mutationFn: (id) => customFetcher<void>({ endpoint: `/tags/${id}/`, method: 'DELETE' }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: TAGS_QUERY_KEY });
            // Tag-Zuordnungen können sich geändert haben.
            void queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
            void queryClient.invalidateQueries({ queryKey: ['calendarEntries'] });
        },
    });
    return { deleteTag, isDeletingTag };
};
